import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUniqueConstraintViolation } from "../dbErrors";
import { normalizeName } from "../normalizeName";
import {
  assertCustomRolePermissions,
  PERMISSIONS,
  RETIRED_PERMISSIONS,
  SCOPE_KINDS,
  type Permission,
} from "../rbac/permissions";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

const MAX_ROLE_NAME_LENGTH = 50;

const roleNameField = z
  .string()
  .min(1, { message: "Role name is required" })
  .max(MAX_ROLE_NAME_LENGTH, {
    message: `Role name must be ${MAX_ROLE_NAME_LENGTH} characters or less`,
  })
  .transform(normalizeName)
  .refine((n) => n.length > 0, {
    message: "Role name must contain at least one visible character",
  });

export const createRole = mutator({
  schema: z.object({
    id: clientGeneratableIdSchema,
    name: roleNameField,
    scopeKind: z.enum(SCOPE_KINDS),
    permissions: z
      .array(z.enum([...PERMISSIONS, ...RETIRED_PERMISSIONS]))
      .min(1),
  }),
  resolve: {},
  permissions: [
    { permission: "tenant:roles_manage", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({ tx, ctx, args }) => {
    const permissions = args.permissions.filter(
      (p): p is Permission =>
        !(RETIRED_PERMISSIONS as readonly string[]).includes(p)
    );
    assertCustomRolePermissions(args.scopeKind, permissions);
    try {
      await tx.mutate.roles.insert({
        id: args.id,
        name: args.name,
        scopeKind: args.scopeKind,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        tenantId: ctx.tenantId,
      });
    } catch (e) {
      if (isUniqueConstraintViolation(e)) {
        throw userFacingError("A role with this name already exists");
      }
      throw e;
    }
    for (const permission of new Set(permissions)) {
      await tx.mutate.rolePermissions.insert({
        roleId: args.id,
        permission,
        tenantId: ctx.tenantId,
      });
    }
  },
});
